import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError, filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DepartmentType } from 'src/app/request-v2/models/request.model';
import { State } from 'src/app/store';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';

export interface ScopeFieldOption {
    value: string;
    labelKey: string; // Translation key for i18n
}

export interface ScopeFieldConfig {
    resourceOptions: ScopeFieldOption[];
    resourceIdOptions: { [resourceType: string]: ScopeFieldOption[] };
    useDropdowns: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class AccessControlScopeConfigService {
    private readonly baseUrl = `${environment.API_GATEWAY_URL}/request-v2/admin`;

    // Cache for departments
    private departmentsCache$: Observable<ScopeFieldOption[]> | null = null;

    // Cache for card types
    private cardTypesCache$: Observable<ScopeFieldOption[]> | null = null;

    constructor(
        private http: HttpClient,
        private store: Store<State>,
    ) {}

    private configs: { [moduleName: string]: ScopeFieldConfig } = {
        REQUEST: {
            useDropdowns: true,
            resourceOptions: [
                { value: 'department', labelKey: 'request_v2.category_list.department' },
                { value: 'settings', labelKey: 'request_v2.user-requests.settings' },
            ],
            resourceIdOptions: {
                department: [], // Will be fetched from API
                settings: [{ value: '*', labelKey: 'request_v2.filter.all' }],
            },
        },
        CARD: {
            useDropdowns: true,
            resourceOptions: [{ value: 'card_type', labelKey: 'pages.userRecords.card-type' }],
            resourceIdOptions: {
                card_type: [], // Will be fetched from org config
            },
        },
    };

    getDepartments(): Observable<DepartmentType[]> {
        return this.http.get<DepartmentType[]>(`${this.baseUrl}/request-department`);
    }

    /**
     * Get the scope field configuration for a specific module
     * @param moduleName The name of the module (e.g., 'REQUEST')
     * @returns The configuration object or null if no special config exists
     */
    getConfig(moduleName: string): ScopeFieldConfig | null {
        return this.configs[moduleName] || null;
    }

    /**
     * Check if a module uses dropdown fields instead of free text inputs
     * @param moduleName The name of the module
     * @returns true if dropdowns should be used, false otherwise
     */
    usesDropdowns(moduleName: string): boolean {
        const config = this.getConfig(moduleName);
        return config?.useDropdowns ?? false;
    }

    /**
     * Get the resource type options for a module
     * @param moduleName The name of the module
     * @returns Observable of resource options
     */
    getResourceOptions(moduleName: string): Observable<ScopeFieldOption[]> {
        const config = this.getConfig(moduleName);
        return of(config?.resourceOptions || []);
    }

    /**
     * Get the resource ID options based on the selected resource type
     * @param moduleName The name of the module
     * @param resourceType The selected resource type (e.g., 'department', 'settings')
     * @returns Observable of resource ID options
     */
    getResourceIdOptions(moduleName: string, resourceType: string): Observable<ScopeFieldOption[]> {
        if (!resourceType) {
            return of([]);
        }

        // Special handling for departments - fetch from API
        if (resourceType === 'department') {
            return this.getDepartmentsAsOptions();
        }

        // Special handling for card types - fetch from org config
        if (resourceType === 'card_type') {
            return this.getCardTypesAsOptions();
        }

        // For other resource types, return static config
        const config = this.getConfig(moduleName);
        return of(config?.resourceIdOptions?.[resourceType] || []);
    }

    /**
     * Get departments from API and map to ScopeFieldOption format
     * @returns Observable of department options with a default '*' option prepended
     */
    private getDepartmentsAsOptions(): Observable<ScopeFieldOption[]> {
        // Use cached observable if available
        if (this.departmentsCache$) {
            return this.departmentsCache$;
        }

        // Default option that always exists
        const defaultOption: ScopeFieldOption = { value: '*', labelKey: 'request_v2.filter.all' };

        // Fetch and cache
        this.departmentsCache$ = this.getDepartments().pipe(
            map((departments) => {
                const departmentOptions = departments.map((dept) => ({
                    value: dept._id,
                    labelKey: dept.name, // Use department name directly as label
                }));
                // Prepend default option to the beginning
                return [defaultOption, ...departmentOptions];
            }),
            catchError(() => of([defaultOption])), // Return only default option on error
            shareReplay(1),
        );

        return this.departmentsCache$;
    }

    /**
     * Get card types from org config and map to ScopeFieldOption format
     * @returns Observable of card type options with a default '*' option prepended
     */
    private getCardTypesAsOptions(): Observable<ScopeFieldOption[]> {
        if (this.cardTypesCache$) {
            return this.cardTypesCache$;
        }

        const defaultOption: ScopeFieldOption = { value: '*', labelKey: 'request_v2.filter.all' };

        this.cardTypesCache$ = this.store.select(organisationConfigurationSelector).pipe(
            filter((config) => !!config),
            take(1),
            map((config) => {
                const cards = config?.support?.cards || [];
                const cardOptions = cards.map((card) => ({
                    value: card.type,
                    labelKey: `pages.userRecords.cardTypes.${card.type}`,
                }));
                return [defaultOption, ...cardOptions];
            }),
            catchError(() => of([defaultOption])),
            shareReplay(1),
        );

        return this.cardTypesCache$;
    }

    /**
     * Clear the departments cache
     */
    clearCache(): void {
        this.departmentsCache$ = null;
        this.cardTypesCache$ = null;
    }
}
